import * as React from "react";
import { withKnobs } from "@storybook/addon-knobs";
import { storiesOf } from "@storybook/react";
import { IAuthorPressPhoto } from "~/types/author-press-photo";
import { withStoryContainer } from "~storybook/custom-decorators/with-story-container";
import Button from "~/components/button/button";
import { useEffect, useState } from "react";

import Spinner from "../spinner/spinner";
import classNames from "classnames";
import IconLink from "../icon-link/icon-link";

const stories = storiesOf("Components|✍️ Author modules.✍️ Author Press", module);
stories.addDecorator(withKnobs);
stories.addDecorator(withStoryContainer);

export interface AuthorPressPhotosProps {
    pressPhotosDescription?: string;
    pressPhotos: IAuthorPressPhoto[];
}

const Image: React.FC<{ src: string; srcSet: string; alt: string }> = ({ src, srcSet, alt }) => {
    const [loading, setLoading] = useState(true);

    return (
        <>
            <div className={classNames(`author-press-photos__spinner`, loading ? "visible" : "hidden")}>
                <Spinner />
            </div>

            <img
                src={src}
                className={classNames(`author-press-photos__photo`, loading ? "hidden" : "visible")}
                sizes="(min-width: 368px) 50vw, (min-width: 544px) 33vw, (min-width: 640px) 50vw, (min-width: 768px) 168px, (min-width: 1152px) 160px, 100vw"
                srcSet={srcSet}
                alt={alt}
                loading="lazy"
                onLoad={() => setLoading(false)}
                decoding="async"
            />
        </>
    );
};

const AuthorPressPhotos: React.FC<AuthorPressPhotosProps> = ({ pressPhotosDescription, pressPhotos }) => {
    const itemsToShow = 20;
    const [photos, setPhotos] = useState<IAuthorPressPhoto[]>([]);
    const [visibleCount, setVisibleCount] = useState(itemsToShow);

    useEffect(() => {
        setPhotos(pressPhotos.slice(0, visibleCount));
    }, [visibleCount]);

    const loadMore = () => {
        setVisibleCount(prevCount => prevCount + itemsToShow);
    };

    const baseClass = "author-press-photos";
    const wrapperClass = `${baseClass}__wrapper`;
    const introText = `${baseClass}__introText`;
    const photoDescriptionClass = `${baseClass}__photoDescription`;
    const photoCommentClass = `${baseClass}__photoComment`;
    const photoPhotographerClass = `${baseClass}__photoPhotographer`;

    return (
        <>
            {pressPhotosDescription && (
                <p className={introText} dangerouslySetInnerHTML={{ __html: pressPhotosDescription }} />
            )}

            <div className={baseClass}>
                {photos.map(photo => {
                    if (!photo || !photo.url || !photo.downloadUrl) {
                        return null;
                    }

                    const src = `${photo.url}&w=400`;
                    const srcSet = `${photo.url}&w=400 400w, ${photo.url}&w=600 600w`;
                    const alt = photo.alt || "";

                    return (
                        <div className={wrapperClass} key={`key-${photo.url}`}>
                            <Image src={src} srcSet={srcSet} alt={alt} />

                            <div className={photoDescriptionClass}>
                                {photo.comment && <div className={photoCommentClass}>{photo.comment}</div>}
                                {photo.description && (
                                    <div className={photoPhotographerClass}>{photo.description}</div>
                                )}
                                <IconLink
                                    href={photo.downloadUrl}
                                    download
                                    target="_blank"
                                    iconId="icon_download"
                                >
                                    Hent foto
                                </IconLink>
                            </div>
                        </div>
                    );
                })}
            </div>

            {pressPhotos.length >= visibleCount && (
                <>
                    <div className="search-results__x-of-y">
                        Viser {visibleCount} ud af {pressPhotos.length} resultater
                    </div>
                    {visibleCount < pressPhotos.length && (
                        <div className="search-results__button-wrapper">
                            <Button
                                className="search-results__button"
                                isLarge={true}
                                isInverted={true}
                                onClick={loadMore}
                            >
                                Hent flere
                            </Button>
                        </div>
                    )}
                </>
            )}
        </>
    );
};

export default AuthorPressPhotos;
